import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Search, Telescope, Hammer, Rocket } from "lucide-react";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { MorphingOrb, TextReveal } from "@/components/PremiumAnimations";
import SEOHead from "@/components/SEOHead";

const phases = [
  {
    icon: Telescope,
    phase: "01",
    title: "Discover",
    duration: "Week 1-2",
    color: "teal",
    description: "We invest time upfront to understand your business, users, and technical landscape before writing a single line of code.",
    activities: [
      "Technical requirements deep-dive with your team",
      "Architecture review and technology recommendations",
      "Risk assessment and compliance requirement mapping",
      "Competitive analysis and market positioning review",
      "Preliminary system design and data modeling",
      "Detailed project roadmap with milestone definitions"
    ],
    deliverables: [
      "Technical Architecture Document",
      "System Design Specifications",
      "Project Timeline with Milestones",
      "Risk Register and Mitigation Plan"
    ]
  },
  {
    icon: Hammer,
    phase: "02",
    title: "Build",
    duration: "Week 3-12",
    color: "orange",
    description: "Iterative development with weekly demos. You see progress every week, not just at the end.",
    activities: [
      "Sprint-based development with 1-week cycles",
      "Weekly demo sessions with your team",
      "Continuous integration and automated testing",
      "Code reviews and quality gates on every merge",
      "Daily standups and progress updates",
      "Ongoing security scanning and performance testing"
    ],
    deliverables: [
      "Working software delivered every sprint",
      "Automated test suites with 90%+ coverage",
      "CI/CD pipeline with staging environment",
      "Technical documentation updated continuously"
    ]
  },
  {
    icon: Rocket,
    phase: "03",
    title: "Scale",
    duration: "Week 12+",
    color: "teal",
    description: "Launch is just the beginning. We ensure your product performs under real-world conditions and grows with your business.",
    activities: [
      "Production deployment with zero-downtime strategy",
      "Load testing and performance optimization",
      "Monitoring and alerting setup",
      "Knowledge transfer and team training sessions",
      "Post-launch support and rapid bug fixes",
      "Growth-phase feature development and scaling"
    ],
    deliverables: [
      "Production-ready deployment",
      "Monitoring dashboards and alerting rules",
      "Runbooks and operational documentation",
      "Knowledge transfer sessions with recordings"
    ]
  }
];

const principles = [
  {
    title: "Transparency Over Everything",
    desc: "Real-time project visibility. You see the same dashboards we do. No information asymmetry."
  },
  {
    title: "Working Software Weekly",
    desc: "Not monthly reports. Not slide decks. Working, testable software every single week."
  },
  {
    title: "Senior Engineers Only",
    desc: "No bait-and-switch. The engineers in the proposal are the engineers on the project."
  },
  {
    title: "Fixed Scope, Fixed Price",
    desc: "Milestone-based payments tied to deliverables. You pay for results, not hours."
  },
  {
    title: "Your Code, Your IP",
    desc: "Full source code ownership from day one. No vendor lock-in. Complete documentation."
  },
  {
    title: "Warranty Included",
    desc: "1 to 6 months of post-launch support depending on scope. Bugs fixed at no extra cost."
  }
];

export default function ApproachPage() {
  return (
    <main data-testid="approach-page">
      <SEOHead
        title="Our Approach | Discover. Build. Scale. — Venury"
        description="A structured framework that turns complex requirements into production software. Discovery, sprint-based build, and post-launch scaling."
        url="https://venury.tech/approach"
      />
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[#0B1116]" />
        <MorphingOrb
          color1="rgba(22, 160, 133, 0.15)"
          color2="rgba(72, 201, 176, 0.08)"
          size={500}
          duration={22}
          style={{ top: "-15%", right: "-10%" }}
        />
        <MorphingOrb
          color1="rgba(230, 126, 34, 0.10)"
          color2="rgba(211, 84, 0, 0.06)"
          size={350}
          duration={28}
          style={{ bottom: "5%", left: "-8%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0B1116]/50 to-[#0B1116]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 w-full">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-8">
            <span className="tag-box font-mono text-sm text-teal-bright uppercase tracking-[0.2em]">
              Our Process
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight"
          >
            Discover. Build. <span className="gradient-text-teal">Scale.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-2xl mt-8"
          >
            A structured framework that turns complex requirements into production software. 
            Every project follows the same rigorous process, adapted to your specific needs.
          </motion.p>
        </div>
      </section>

      {/* Process Phases */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-40">
            {phases.map((phase, i) => {
              const PhaseIcon = phase.icon;
              return (
                <ScrollReveal key={i}>
                  {i > 0 && (
                    <div className="w-full border-t border-white/5 mb-16" />
                  )}
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
                    {/* Phase Header */}
                    <div className="lg:col-span-4">
                      <div>
                        <div className={`inline-flex items-center gap-3 mb-6`}>
                          <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                            phase.color === "teal" ? "bg-teal-primary/10" : "bg-orange-primary/10"
                          }`}>
                            <PhaseIcon size={26} className={phase.color === "teal" ? "text-teal-bright" : "text-orange-bright"} />
                          </div>
                          <div>
                            <span className="font-mono text-xs text-slate-600 uppercase tracking-widest block">Phase {phase.phase}</span>
                            <span className="font-mono text-xs text-slate-600">{phase.duration}</span>
                          </div>
                        </div>

                        <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
                          {phase.title}
                        </h2>

                        <p className="text-base text-slate-400 leading-relaxed">
                          {phase.description}
                        </p>
                      </div>
                    </div>

                    {/* Phase Content */}
                    <div className="lg:col-span-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="glass-card rounded-lg p-6 h-full flex flex-col">
                          <h4 className="font-mono text-xs text-teal-bright uppercase tracking-widest mb-5 flex-shrink-0">
                            Activities
                          </h4>
                          <ul className="space-y-3 flex-1">
                            {phase.activities.map((activity, j) => (
                              <li key={j} className="flex items-start gap-3 text-sm text-slate-400">
                                <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${
                                  phase.color === "teal" ? "bg-teal-bright" : "bg-orange-bright"
                                }`} />
                                {activity}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="glass-card rounded-lg p-6 h-full flex flex-col">
                          <h4 className="font-mono text-xs text-orange-bright uppercase tracking-widest mb-5 flex-shrink-0">
                            Deliverables
                          </h4>
                          <ul className="space-y-3 flex-1">
                            {phase.deliverables.map((deliverable, j) => (
                              <li key={j} className="flex items-start gap-3 text-sm text-slate-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-orange-bright mt-2 flex-shrink-0" />
                                {deliverable}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-20 md:py-32 bg-[#080C10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">Principles</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight mb-16">
              How We Work
            </h2>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" staggerDelay={0.08}>
            {principles.map((item, i) => (
              <StaggerItem key={i}>
                <div className="p-6 rounded-lg border border-white/5 hover:border-teal-primary/20 transition-colors duration-300 h-full">
                  <h3 className="font-heading text-lg font-semibold text-white mb-3">{item.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>
    </main>
  );
}
